
import Vue from "vue";
import { Manuscript, ManuscriptSetting } from "@/lib/models";
import { getEffectiveEditorTheme } from "@/lib/utils/theme";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    novelId: {
      type: String,
      required: true,
    },
    manuscriptList: Array as Object,
  },
  data() {
    return {
      previewWidth: 0,
      resizeObserver: null,
    };
  },
  async mounted() {
    /** ResizeObserverを定義 */
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.previewWrapper);

    this.onResize();
    this.$refs.previewWrapper.scroll(this.previewWidth, 0);
  },
  destroyed() {
    this.resizeObserver.disconnect();
  },
  watch: {
    scrollRatio() {
      this.$refs.previewWrapper.scroll({
        left: this.previewWidth * (1 - this.scrollRatio),
      });
    },
    manuscriptList: {
      handler() {
        this.onResize();
      },
      deep: true,
    },
  },
  computed: {
    theme() {
      const storeTheme = this.$store.getters["manuscriptModule/theme"];
      const effectiveAppTheme = this.$store.getters["generalModule/effectiveTheme"] as "light" | "dark";
      return getEffectiveEditorTheme(storeTheme, effectiveAppTheme);
    },
    manuscriptSetting() {
      return this.$store.getters["novelModule/manuscriptSetting"](this.novelId) as ManuscriptSetting;
    },
    htmlContent() {
      return (content) => {
        const splittedContent = content.split("\n");
        const addedRubyContent = splittedContent.map((content: string) => {
          // eslint-disable-next-line no-control-regex
          const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
          const textWithRuby = content.match(rubyRegExp);
          if (textWithRuby) {
            textWithRuby.forEach((text: string) => {
              let replaceText = text;
              const parentRegExp = new RegExp("[|｜]");
              replaceText = replaceText.replace(parentRegExp, "<ruby>");
              replaceText = replaceText.replace("《", "<rt>");
              replaceText = replaceText.replace("》", "</rt></ruby>");
              // eslint-disable-next-line no-param-reassign
              content = content.replace(text, replaceText);
            });
          }

          return this.processContentWithLineBreaks(content);
        });
        const wrappedContent = addedRubyContent.map((content: string) => `<p>${content}</p>`);
        return wrappedContent.join("");
      };
    },
    styles() {
      // eslint-disable-next-line prefer-const
      const { wordCountOnVertical, fontSizeRate } = this.manuscriptSetting as ManuscriptSetting;

      return {
        "--word-count-on-vertical": `${wordCountOnVertical}.5em`,
        "--font-size-rate": fontSizeRate,
      };
    },
    scrollRatio() {
      return this.$store.getters["manuscriptModule/scrollRatio"];
    },
  },
  methods: {
    handleScroll(e, position) {
      const scrollRatio = 1 - position.scrollLeft / this.previewWidth;
      this.$store.dispatch("manuscriptModule/updateScrollRatio", scrollRatio);
    },
    onResize() {
      const { previewWrapper } = this.$refs;
      this.previewWidth = previewWrapper.scrollWidth - previewWrapper.clientWidth;
    },
    processContentWithLineBreaks(content: string) {
      const { wordCountOnVertical } = this.manuscriptSetting;

      const lines: string[] = [];
      let currentLine = "";
      let currentCount = 0;
      let insideRtTag = false; // <rt> タグ内かどうかを判定

      // rubyタグを考慮しながら文字を処理
      content.replace(/(<[^>]+>|.)/g, (match) => {
        if (match.startsWith("<")) {
          // タグの開始・終了を判定
          if (match.startsWith("<rt>")) {
            insideRtTag = true;
          } else if (match.startsWith("</rt>")) {
            insideRtTag = false;
          }

          // タグはそのまま追加
          currentLine += match;
        } else if (!insideRtTag) {
          // プレーンな文字をカウント（<rt> タグ内はカウントしない）
          currentLine += match;
          currentCount += 1;
        } else {
          // <rt> タグ内の文字はそのまま追加するがカウントしない
          currentLine += match;
        }

        // 指定文字数を超えたら改行
        if (currentCount >= wordCountOnVertical) {
          lines.push(currentLine);
          currentLine = ""; // 次の行を開始
          currentCount = 0;
        }

        return match;
      });

      // 最後の行を追加
      if (currentLine) {
        lines.push(currentLine);
      }

      // 各行を `<br>` で結合して返す
      return lines.join("<br>");
    },
  },
});

interface Props {
  novelId: string;
  manuscriptList: Manuscript[];
}

interface Data {
  previewWidth: number;
  resizeObserver: ResizeObserver | null;
}

interface Computed {
  theme: string;
  manuscriptSetting: ManuscriptSetting;
  htmlContent: (content: string) => string;
  styles: object;
  scrollRatio: number;
}

interface Methods {
  handleScroll(e: Event, position: any): void;
  onResize(): void;
  processContentWithLineBreaks(content: string): string;
}
